"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CleanupPolicyValidationError = void 0;
exports.validateCleanupPolicy = validateCleanupPolicy;
exports.resolveCleanupBinding = resolveCleanupBinding;
exports.readCleanupRunResult = readCleanupRunResult;
exports.cleanupBindingConfiguration = cleanupBindingConfiguration;
exports.validateCleanupTimezone = validateCleanupTimezone;
const database_view_filter_1 = require("./database-view-filter");
class CleanupPolicyValidationError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'CleanupPolicyValidationError';
    }
}
exports.CleanupPolicyValidationError = CleanupPolicyValidationError;
const numberOperators = new Set([
    'before',
    'after',
    'equal',
    'notEqual',
    'greatThan',
    'lessThan',
    'greatThanOrEqual',
    'lessThanOrEqual',
    'countEqual',
    'countNotEqual',
    'countGreaterThan',
    'countGreaterThanOrEqual',
    'countLessThan',
    'countLessThanOrEqual',
]);
const arrayOperators = new Set([
    'isOneOf',
    'isNotOneOf',
    'containsOneOf',
    'doesNotContainOneOf',
    'containsAll',
    'doesNotContainAll',
]);
const noArgOperators = new Set([
    'isEmpty',
    'isNotEmpty',
    'isChecked',
    'isUnchecked',
]);
function record(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function validateFilter(value) {
    if (!record(value))
        throw new CleanupPolicyValidationError('Invalid cleanup filter');
    if (value.type === 'group') {
        if ((value.op !== 'and' && value.op !== 'or') ||
            !Array.isArray(value.conditions) ||
            !value.conditions.length) {
            throw new CleanupPolicyValidationError('Cleanup filter groups must contain a rule');
        }
        value.conditions.forEach(validateFilter);
        return;
    }
    if (value.type !== 'filter' ||
        !record(value.left) ||
        value.left.type !== 'ref' ||
        typeof value.left.name !== 'string' ||
        !value.left.name.trim() ||
        typeof value.function !== 'string' ||
        !(0, database_view_filter_1.getDatabaseViewFilterFunction)(value.function) ||
        !Array.isArray(value.args)) {
        throw new CleanupPolicyValidationError('Invalid cleanup filter reference or operator');
    }
    const operator = value.function;
    const expectedArgs = noArgOperators.has(operator) ? 0 : 1;
    if (value.args.length !== expectedArgs ||
        value.args.some(arg => !record(arg) || arg.type !== 'literal')) {
        throw new CleanupPolicyValidationError('Invalid cleanup filter arguments');
    }
    if (!expectedArgs)
        return;
    const arg = value.args[0].value;
    const valid = operator === 'olderThanDays'
        ? typeof arg === 'number' && Number.isSafeInteger(arg) && arg > 0
        : numberOperators.has(operator)
            ? typeof arg === 'number' && Number.isFinite(arg)
            : arrayOperators.has(operator)
                ? Array.isArray(arg) && arg.every(item => typeof item === 'string')
                : typeof arg === 'string';
    if (!valid)
        throw new CleanupPolicyValidationError('Invalid cleanup filter argument type');
}
/** Destructive selection never interprets an empty/incomplete group as all rows. */
function validateCleanupPolicy(policy) {
    if (!record(policy) ||
        !record(policy.filter) ||
        policy.filter.type !== 'group') {
        throw new CleanupPolicyValidationError('Cleanup requires a filter group');
    }
    validateFilter(policy.filter);
    if (policy.filter.type !== 'group')
        throw new CleanupPolicyValidationError('Cleanup requires a filter group');
    return { filter: policy.filter };
}
/** The selected side always replaces the entire binding, never only one port. */
function resolveCleanupBinding(input, side) {
    if (side !== 'input' && side !== 'output')
        throw new Error('Invalid cleanup side');
    for (const id of [input.workspaceId, input.docId, input.databaseId]) {
        if (typeof id !== 'string' || !id.trim())
            throw new Error('Invalid cleanup binding');
    }
    return { input: { ...input }, output: { ...input } };
}
/** Read acknowledged counters from persisted/runtime logs without guessing. */
function readCleanupRunResult(value) {
    if (!record(value) ||
        (value.status !== 'completed' && value.status !== 'partial') ||
        ['deleted', 'skippedChanged', 'skippedMissing'].some(key => typeof value[key] !== 'number' ||
            !Number.isSafeInteger(value[key]) ||
            value[key] < 0) ||
        (value.error !== undefined && typeof value.error !== 'string'))
        return null;
    return {
        deleted: value.deleted,
        skippedChanged: value.skippedChanged,
        skippedMissing: value.skippedMissing,
        status: value.status,
        ...(typeof value.error === 'string' ? { error: value.error } : {}),
    };
}
/** Shared binding change for the editor and server automation writer. */
function cleanupBindingConfiguration(workspaceId, props, binding, side, tableName = '', columns = []) {
    if (props.executionKind !== 'cleanup') {
        throw new Error('Cleanup binding requires a cleanup block');
    }
    if (binding && binding.workspaceId !== workspaceId) {
        throw new Error('Cleanup database must belong to the same workspace');
    }
    const resolved = binding ? resolveCleanupBinding(binding, side) : null;
    const modified = columns.find(column => column.type === 'updated-time');
    // Initialize the first binding only. A saved rule remains an explicit choice.
    const filterGroup = resolved && modified && props.filterGroup.conditions.length === 0
        ? {
            type: 'group',
            op: 'and',
            conditions: [
                {
                    type: 'filter',
                    left: { type: 'ref', name: modified.id },
                    function: 'olderThanDays',
                    args: [{ type: 'literal', value: 30 }],
                },
            ],
        }
        : props.filterGroup;
    return {
        filterGroup,
        sourceTableId: resolved?.input.databaseId ?? null,
        sourceDocId: resolved?.input.docId ?? null,
        sourceTableName: resolved ? tableName : null,
        outputTables: resolved
            ? [
                {
                    tableId: resolved.output.databaseId,
                    docId: resolved.output.docId,
                    tableName,
                },
            ]
            : [],
        inputTables: [],
        outputMode: 'update_source',
        triggerConfig: { ...props.triggerConfig, enabled: false },
    };
}
/** Shared timezone validation for the cleanup editor and schedule command. */
function validateCleanupTimezone(timezone) {
    new Intl.DateTimeFormat(undefined, { timeZone: timezone });
}
